import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Globe, MapPin, AlertTriangle, Code, Wrench, Bot, Eye, Edit, ChevronDown, ChevronRight, ExternalLink, Save, X, Server, BookOpen, Sparkles } from 'lucide-react';
import { AgentTool, Agent, CreateAgentForm, BuiltinCredentialStatus, CreateModelForm } from '../../types';
import AgentToolSelector from './AgentToolSelector';
import AgentFormModal from './AgentFormModal';
import AgentDetailsModal from './AgentDetailsModal';
import MCPToolSelector from './MCPToolSelector';
import KnowledgeSearchToolSelector from './KnowledgeSearchToolSelector';
import GeneratedSchemaReviewModal from './GeneratedSchemaReviewModal';
import { useProjectStore } from '../../store';
import Modal from '../common/Modal';
import Tooltip from '../common/Tooltip';
import apiService from '../../services/api';
import { inferSchemaFromJson, InferredProperty, SuggestedChildSchema } from '../../utils/inferSchemaFromJson';
import { useAgents } from '../../hooks/queries/useAgents';
import { useModels } from '../../hooks/queries/useModels';
import { useCreateAgent, useUpdateAgent } from '../../hooks/queries/useAgentMutations';
import { useCreateModel } from '../../hooks/queries/useModelMutations';

const EMAIL_SERVER_ID = -2;

interface ToolManagementSystemProps {
  tools: AgentTool[];
  onToolsChange: (tools: AgentTool[]) => void;
  availableAgents?: Agent[];
  currentAgentId?: number;
}

const ToolManagementSystem: React.FC<ToolManagementSystemProps> = ({
  tools,
  onToolsChange,
  availableAgents = [],
  currentAgentId
}) => {
  const [showAddTool, setShowAddTool] = useState(false);
  const [showAgentToolSelector, setShowAgentToolSelector] = useState(false);
  const [showCreateToolAgentModal, setShowCreateToolAgentModal] = useState(false);
  const [lastCreatedToolAgentId, setLastCreatedToolAgentId] = useState<number | null>(null);
  const [expandedAgentTools, setExpandedAgentTools] = useState<Set<number | string>>(new Set());
  const [showAgentDetailsModal, setShowAgentDetailsModal] = useState(false);
  const [selectedAgentForDetails, setSelectedAgentForDetails] = useState<Agent | null>(null);
  const [showEditAgentModal, setShowEditAgentModal] = useState(false);
  const [selectedAgentForEdit, setSelectedAgentForEdit] = useState<Agent | null>(null);
  // AC-1411 / AC-1377 P4.5: Read agents/models through canonical RQ hooks.
  // Mutation hooks invalidate `projectAgents` / `projectModels` /
  // `projectRegistries` automatically on success - no manual `loadAgents()`
  // / `loadModels()` reloads needed.
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.project_id;
  const agentsQuery = useAgents(projectId);
  const modelsQuery = useModels(projectId);
  const agents = agentsQuery.data ?? [];
  const models = modelsQuery.data ?? [];
  const createAgentMutation = useCreateAgent(projectId ?? '');
  const updateAgentMutation = useUpdateAgent(projectId ?? '');
  const createModelMutation = useCreateModel(projectId ?? '');
  const [editingToolIndex, setEditingToolIndex] = useState<number | null>(null);
  const [editingToolName, setEditingToolName] = useState('');
  const [editingToolDescription, setEditingToolDescription] = useState('');
  const [emailCredentials, setEmailCredentials] = useState<BuiltinCredentialStatus[]>([]);
  const [godaddyToolDefs, setGodaddyToolDefs] = useState<Record<string, { description: string; input_description?: string; example_output: string; notes?: string }>>({});
  const [expandedToolExamples, setExpandedToolExamples] = useState<Set<number>>(new Set());
  // Schema generation from tool output
  const [generatingSchemaFor, setGeneratingSchemaFor] = useState<{
    parentClassName: string;
    parentDescription: string;
    parentProperties: InferredProperty[];
    suggestedChildren: SuggestedChildSchema[];
  } | null>(null);
  const [generateSchemaError, setGenerateSchemaError] = useState<string | null>(null);

  // Load GoDaddy tool definitions so we can show richer info in attached tools
  const hasGodaddyTools = tools.some((t) => t.type?.startsWith('godaddy_'));
  useEffect(() => {
    if (!hasGodaddyTools) return;
    apiService.getGoDaddyToolDefinitions()
      .then((res) => {
        const map: Record<string, { description: string; input_description?: string; example_output: string; notes?: string }> = {};
        res.tools.forEach((t) => {
          map[t.type] = { description: t.description, input_description: t.input_description, example_output: t.example_output, notes: t.notes };
        });
        setGodaddyToolDefs(map);
      })
      .catch(() => {});
  }, [hasGodaddyTools]);

  const toggleToolExample = (index: number) => {
    setExpandedToolExamples((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  /**
   * Generate a set of schemas from a tool's example output JSON.
   * Infers the root shape + any nested child shapes, then opens a review
   * modal where the user can edit, rename, or delete child schemas before
   * saving. Child schemas are saved first so the parent can reference them.
   */
  const handleGenerateSchemaFromTool = (toolName: string, exampleOutput: string) => {
    setGenerateSchemaError(null);
    try {
      // Suggest a class name based on the tool (e.g. "check_domain_availability" -> "DomainAvailabilityResult")
      const toolSuffix = toolName
        .replace(/^godaddy_/, '')
        .replace(/^check_/, '')
        .replace(/^get_/, '');
      const pascal = toolSuffix
        .split(/[_\s]+/)
        .map((p) => (p.length === 0 ? '' : p.charAt(0).toUpperCase() + p.slice(1)))
        .join('');
      const suggestedName = pascal ? `${pascal}Result` : 'ToolOutput';

      const { properties, suggestedChildren } = inferSchemaFromJson(exampleOutput, suggestedName);
      setGeneratingSchemaFor({
        parentClassName: suggestedName,
        parentDescription: `Schema generated from ${toolName} tool output`,
        parentProperties: properties,
        suggestedChildren,
      });
    } catch (err) {
      setGenerateSchemaError(
        err instanceof Error ? err.message : 'Could not parse example output'
      );
    }
  };

  /**
   * Save generated schemas in sequence via the RQ create-model mutation.
   * Children come first (list already ordered that way), then the parent.
   * The mutation hook auto-invalidates `projectModels` and
   * `projectRegistries` on each success - no manual `loadModels()` reload
   * needed.
   */
  const handleGeneratedSchemasSave = async (payloads: CreateModelForm[]) => {
    if (!currentProject) return;
    for (const payload of payloads) {
      await createModelMutation.mutateAsync({ data: payload });
    }
  };

  // Load email credentials so we can show which account is bound to email tools
  const hasEmailTools = tools.some((t) => t.mcp_server_id === EMAIL_SERVER_ID);
  useEffect(() => {
    if (!hasEmailTools) return;
    apiService.listBuiltinCredentials(EMAIL_SERVER_ID)
      .then(setEmailCredentials)
      .catch(() => {});
  }, [hasEmailTools]);

  console.log('[ToolManagementSystem] Rendered with:', {
    toolsCount: tools.length,
    availableAgentsCount: availableAgents.length,
    currentAgentId,
    showAddTool,
    showAgentToolSelector,
    showCreateToolAgentModal,
    lastCreatedToolAgentId
  });

  // Get the latest tool agents from the store
  const toolAgents = agents; // Any agent can be used as a tool
  // Use store agents if available, otherwise fall back to prop
  const availableAgentsForSelector = agents.length > 0 ? agents : availableAgents;

  console.log('[ToolManagementSystem] Computed state:', {
    toolAgentsCount: toolAgents.length,
    availableAgentsForSelectorCount: availableAgentsForSelector.length,
    agentsFromStore: agents.length,
    agentsFromProps: availableAgents.length,
    currentProject: currentProject?.project_id
  });

  // AC-1411: agents auto-fetch via the `useAgents(projectId)` hook above
  // when the cache is empty. The seed effect that called `loadAgents` is
  // gone - the hook handles initial load + dedupe internally.

  const [newToolType, setNewToolType] = useState<'web_search'>('web_search');
  const [newLocation, setNewLocation] = useState<{ city: string; region: string; country: string }>({ city: '', region: '', country: '' });

  // Check if a tool of the specified type already exists
  const hasToolOfType = (type: string) => {
    return tools.some(tool => tool.type === type);
  };

  // Validate country code (must be exactly 2 characters, letters only)
  const validateCountryCode = (code: string): boolean => {
    return /^[A-Za-z]{2}$/.test(code);
  };

  // Format country code (uppercase, max 2 characters)
  const formatCountryCode = (code: string): string => {
    return code.toUpperCase().slice(0, 2);
  };

  // Get agent details for a tool agent
  const getAgentForTool = (tool: AgentTool): Agent | null => {
    if (tool.type === 'agent' && tool.agent_as_a_tool_id) {
      console.log('[ToolManagementSystem] Looking for agent with ID:', tool.agent_as_a_tool_id);
      console.log('[ToolManagementSystem] Available agents from store:', agents.map(a => ({ id: a.agent_id, name: a.name })));
      console.log('[ToolManagementSystem] Available agents from props:', availableAgents.map(a => ({ id: a.agent_id, name: a.name })));
      console.log('[ToolManagementSystem] Looking for agent ID:', tool.agent_as_a_tool_id, 'Type:', typeof tool.agent_as_a_tool_id);
      console.log('[ToolManagementSystem] Available agent IDs from store:', agents.map(a => a.agent_id));
      console.log('[ToolManagementSystem] Available agent IDs from props:', availableAgents.map(a => a.agent_id));
      
      // Try to find in store agents first, then fall back to props
      let foundAgent = agents.find(agent => agent.agent_id.toString() === tool.agent_as_a_tool_id);
      if (!foundAgent) {
        foundAgent = availableAgents.find(agent => agent.agent_id.toString() === tool.agent_as_a_tool_id);
      }
      
      // Also try with numeric comparison
      if (!foundAgent) {
        const targetId = parseInt(tool.agent_as_a_tool_id);
        if (!isNaN(targetId)) {
          foundAgent = agents.find(agent => agent.agent_id === targetId);
          if (!foundAgent) {
            foundAgent = availableAgents.find(agent => agent.agent_id === targetId);
          }
        }
      }
      
      console.log('[ToolManagementSystem] Found agent:', foundAgent);
      if (!foundAgent) {
        console.log('[ToolManagementSystem] WARNING: Agent with ID', tool.agent_as_a_tool_id, 'not found in any available agents list');
      }
      return foundAgent || null;
    }
    return null;
  };

  // Toggle expanded state for agent tools
  const toggleAgentToolExpansion = (toolIndex: number) => {
    setExpandedAgentTools(prev => {
      const newSet = new Set(prev);
      if (newSet.has(toolIndex)) {
        newSet.delete(toolIndex);
      } else {
        newSet.add(toolIndex);
      }
      return newSet;
    });
  };

  // Handle viewing agent details
  const handleViewAgentDetails = (agent: Agent) => {
    console.log('[ToolManagementSystem] handleViewAgentDetails called with agent:', agent);
    setSelectedAgentForDetails(agent);
    setShowAgentDetailsModal(true);
  };

  // Handle editing agent
  const handleEditAgent = (agent: Agent) => {
    console.log('[ToolManagementSystem] handleEditAgent called with agent:', agent);
    setSelectedAgentForEdit(agent);
    setShowEditAgentModal(true);
  };

  // Handle agent edit completion
  const handleAgentEditComplete = async (agentData: CreateAgentForm) => {
    try {
      console.log('[ToolManagementSystem] handleAgentEditComplete called with agentData:', agentData);
      console.log('[ToolManagementSystem] selectedAgentForEdit:', selectedAgentForEdit);

      if (!selectedAgentForEdit) {
        console.error('[ToolManagementSystem] No agent selected for edit');
        throw new Error('No agent selected for edit');
      }

      // AC-1411: Route through the RQ mutation hook. It auto-invalidates
      // `queryKeys.projectAgents(projectId)` on success so the agents list
      // refresh is automatic - no manual `loadAgents()` reload needed.
      console.log('[ToolManagementSystem] Calling updateAgent mutation with agentId:', selectedAgentForEdit.agent_id);
      const updatedAgent = await updateAgentMutation.mutateAsync({
        agentId: selectedAgentForEdit.agent_id,
        data: agentData,
      });

      console.log('[ToolManagementSystem] Agent updated successfully:', updatedAgent);

      setShowEditAgentModal(false);
      setSelectedAgentForEdit(null);
    } catch (error) {
      console.error('[ToolManagementSystem] Failed to update agent:', error);
      throw error; // Re-throw to let the AgentFormModal handle the error display
    }
  };

  // Handle agent details modal close
  const handleAgentDetailsClose = () => {
    setShowAgentDetailsModal(false);
    setSelectedAgentForDetails(null);
  };

  // Handle agent edit modal close
  const handleAgentEditClose = () => {
    setShowEditAgentModal(false);
    setSelectedAgentForEdit(null);
  };

  const addWebSearchTool = () => {
    const { city, region, country } = newLocation;
    const shouldIncludeLocation = [city, region, country].some(v => v && v.trim() !== '');
    const newTool: AgentTool = shouldIncludeLocation
      ? {
          type: 'web_search',
          name: 'Web Search',
          description: 'Search the web for current information',
          user_location: {
            type: 'approximate',
            city,
            region,
            country
          }
        }
      : {
          type: 'web_search',
          name: 'Web Search',
          description: 'Search the web for current information'
        };
    onToolsChange([...tools, newTool]);
    setShowAddTool(false);
    setNewLocation({ city: '', region: '', country: '' });
  };

  const addSimplifiedTool = () => {
    if (newToolType === 'web_search') {
      addWebSearchTool();
    }
  };

  const removeTool = (index: number) => {
    const updatedTools = tools.filter((_, i) => i !== index);
    onToolsChange(updatedTools);
  };

  const updateTool = (index: number, updatedTool: AgentTool) => {
    if (updatedTool.type === 'web_search' && updatedTool.user_location) {
      const { city, region, country } = updatedTool.user_location;
      const shouldIncludeLocation = [city, region, country].some(v => v && v.trim() !== '');
      if (!shouldIncludeLocation) {
        const { user_location, ...rest } = updatedTool;
        onToolsChange(tools.map((tool, i) => i === index ? rest as AgentTool : tool));
        return;
      }
    }
    onToolsChange(tools.map((tool, i) => i === index ? updatedTool : tool));
  };

  const updateLocation = (index: number, field: 'city' | 'region' | 'country', value: string) => {
    if (!tools[index].user_location && value.trim() === '') return;
    const baseLocation = tools[index].user_location || { type: 'approximate', city: '', region: '', country: '' };
    const updatedLocation = { ...baseLocation, [field]: value };
    const updatedTool = { ...tools[index], user_location: updatedLocation };
    updateTool(index, updatedTool);
  };

  // Handler for creating a new tool agent
  const handleCreateToolAgent = () => {
    console.log('[ToolManagementSystem] handleCreateToolAgent called');
    setShowCreateToolAgentModal(true);
  };

  // Handler for after a tool agent is created
  const handleToolAgentCreated = async (agentData: CreateAgentForm) => {
    console.log('[ToolManagementSystem] handleToolAgentCreated called with agentData:', agentData);
    if (currentProject?.project_id) {
      try {
        // AC-1411: Route through the RQ create-agent mutation. It auto-
        // invalidates `queryKeys.projectAgents(projectId)` on success, so
        // the agents list (and any other consumer of the same key)
        // refreshes automatically - no manual `loadAgents()` reload.
        console.log('[ToolManagementSystem] Creating agent in project:', currentProject.project_id);
        const newAgent = await createAgentMutation.mutateAsync({ data: agentData });
        console.log('[ToolManagementSystem] Agent created successfully:', newAgent);
        setLastCreatedToolAgentId(newAgent.agent_id);
        console.log('[ToolManagementSystem] Set lastCreatedToolAgentId to:', newAgent.agent_id);
      } catch (err) {
        console.error('[ToolManagementSystem] Failed to create agent', err);
        return;
      }
    }
    console.log('[ToolManagementSystem] Closing tool agent creation modal and reopening selector');
    setShowCreateToolAgentModal(false);
    setShowAgentToolSelector(true);
  };

  // Handler for when a tool agent is selected
  const handleAgentToolSelected = (agent: Agent, toolName: string, description: string) => {
    console.log('[ToolManagementSystem] handleAgentToolSelected called with:', {
      agentId: agent.agent_id,
      agentName: agent.name,
      toolName,
      description,
      currentToolsCount: tools.length
    });

    const newTool: AgentTool = {
      type: 'agent',
      name: toolName,
      description: description,
      agent_as_a_tool_id: agent.agent_id.toString()
    };

    console.log('[ToolManagementSystem] Creating new tool:', newTool);
    onToolsChange([...tools, newTool]);
    console.log('[ToolManagementSystem] Tools updated, new count:', tools.length + 1);
    
    console.log('[ToolManagementSystem] Closing selector and resetting lastCreatedToolAgentId');
    setShowAgentToolSelector(false);
    setLastCreatedToolAgentId(null);
  };

  const startEditingTool = (index: number) => {
    const tool = tools[index];
    setEditingToolIndex(index);
    setEditingToolName(tool.name || '');
    setEditingToolDescription(tool.description || '');
  };

  const saveToolEdit = () => {
    if (editingToolIndex !== null) {
      const updatedTool = {
        ...tools[editingToolIndex],
        name: editingToolName.trim(),
        description: editingToolDescription.trim()
      };
      updateTool(editingToolIndex, updatedTool);
      setEditingToolIndex(null);
      setEditingToolName('');
      setEditingToolDescription('');
    }
  };

  const cancelToolEdit = () => {
    setEditingToolIndex(null);
    setEditingToolName('');
    setEditingToolDescription('');
  };

  return (
    <div className="space-y-4">
      {/* Existing Tools */}
      {tools.some((t) => t.type !== 'registry') && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Attached Tools</h4>

          {/* ── Grouped MCP server tools ─────────────────────── */}
          {(() => {
            // Group MCP tools by server ID or parsed server name
            const mcpGroups = new Map<string, { serverName: string; tools: { tool: AgentTool; index: number }[] }>();
            const nonMcpTools: { tool: AgentTool; index: number }[] = [];

            tools.forEach((tool, index) => {
              if (tool.type === 'mcp') {
                const serverName = tool.name?.split(': ')[0] || 'MCP Server';
                const groupKey = tool.mcp_server_id != null ? String(tool.mcp_server_id) : `name:${serverName}`;
                const group = mcpGroups.get(groupKey) || {
                  serverName,
                  tools: [],
                };
                group.tools.push({ tool, index });
                mcpGroups.set(groupKey, group);
              } else if (tool.type === 'registry') {
                // Registry tools are managed in the dedicated Registry Tools section below.
                // Suppressing them here avoids a disjointed duplicate row that just shows "registry / registry".
              } else {
                nonMcpTools.push({ tool, index });
              }
            });

            return (
              <>
                {Array.from(mcpGroups.entries()).map(([serverId, group]) => {
                  const expandKey = `mcp:${serverId}`;
                  const isExpanded = expandedAgentTools.has(expandKey);
                  return (
                    <div key={`mcp-group-${serverId}`} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-900">
                      <div className="flex items-center p-3">
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedAgentTools(prev => {
                              const next = new Set(prev);
                              if (next.has(expandKey)) next.delete(expandKey);
                              else next.add(expandKey);
                              return next;
                            });
                          }}
                          className="flex-shrink-0 mr-2 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                        <Server className="w-4 h-4 text-primary-500 mr-2 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 dark:text-warm-white">
                          {group.serverName}
                        </span>
                        {/* Show which email account is bound */}
                        {serverId === String(EMAIL_SERVER_ID) && (() => {
                          const toolWithCred = group.tools.find(
                            ({ tool }) => tool.tool_config && 'credential_id' in tool.tool_config
                          );
                          if (toolWithCred) {
                            const credId = (toolWithCred.tool.tool_config as { credential_id: number })?.credential_id;
                            const cred = emailCredentials.find(
                              (c) => c.credential_id === credId
                            );
                            if (cred) {
                              return (
                                <span className="ml-2 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded">
                                  {cred.instance_label || cred.email || cred.provider}
                                </span>
                              );
                            }
                          }
                          // If tool had a credential_id but it wasn't found, credential was deleted
                          if (toolWithCred) {
                            return (
                              <Tooltip content="The linked email account was deleted. Use the MCP Server Tools section below to re-select an account.">
                                <span className="ml-2 text-xs text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 px-1.5 py-0.5 rounded cursor-help">
                                  Account deleted
                                </span>
                              </Tooltip>
                            );
                          }
                          // Fallback: extract from tool name "Email (Yahoo Mail): tool_name"
                          const namedTool = group.tools.find(
                            ({ tool }) => tool.name?.includes('(')
                          );
                          if (namedTool) {
                            const match = namedTool.tool.name?.match(/\((.+?)\)/);
                            if (match) {
                              return (
                                <span className="ml-2 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded">
                                  {match[1]}
                                </span>
                              );
                            }
                          }
                          // No credential binding — infer from available credentials
                          if (emailCredentials.length === 1) {
                            const cred = emailCredentials[0];
                            return (
                              <span className="ml-2 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded">
                                {cred.instance_label || cred.email || cred.provider}
                              </span>
                            );
                          }
                          if (emailCredentials.length > 0) {
                            return (
                              <Tooltip content="Use the MCP Server Tools section below to select an account">
                                <span className="ml-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded cursor-help">
                                  Select account
                                </span>
                              </Tooltip>
                            );
                          }
                          return null;
                        })()}
                        <span className="flex-1" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                          {group.tools.length} {group.tools.length === 1 ? 'tool' : 'tools'}
                        </span>
                        <Tooltip content="Remove all tools from this server">
                          <button
                            type="button"
                            onClick={() => {
                              // Remove all tools in this MCP group
                              const toolIndices = new Set(group.tools.map(t => t.index));
                              onToolsChange(tools.filter((_, i) => !toolIndices.has(i)));
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-700 pt-2 space-y-1">
                          {group.tools.map(({ tool, index }) => (
                            <div key={index} className="flex items-center py-1.5 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-sm">
                              <Wrench className="w-3.5 h-3.5 text-gray-400 mr-2 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="text-gray-900 dark:text-warm-white font-medium">
                                  {tool.mcp_tool_name || tool.name}
                                </span>
                              </div>
                              <Tooltip content="Remove tool">
                                <button
                                  type="button"
                                  onClick={() => removeTool(index)}
                                  className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </Tooltip>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* ── Non-MCP tools (rendered individually as before) ── */}
                {nonMcpTools.map(({ tool, index }) => (
            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="text-primary-500">
                    {tool.type === 'web_search' ? (
                      <Globe className="w-4 h-4" />
                    ) : tool.type === 'function' ? (
                      <Code className="w-4 h-4" />
                    ) : tool.type === 'agent' ? (
                      <Bot className="w-4 h-4" />
                    ) : tool.type === 'knowledge_search' ? (
                      <BookOpen className="w-4 h-4 text-amber-600" />
                    ) : (
                      <Wrench className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    {editingToolIndex === index ? (
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Tool Name
                          </label>
                          <input
                            type="text"
                            value={editingToolName}
                            onChange={(e) => setEditingToolName(e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Enter tool name..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Tool Description
                          </label>
                          <textarea
                            value={editingToolDescription}
                            onChange={(e) => setEditingToolDescription(e.target.value)}
                            rows={2}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Enter tool description..."
                          />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={saveToolEdit}
                            className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500"
                          >
                            <Save className="w-3 h-3 inline mr-1" />
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={cancelToolEdit}
                            className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-500"
                          >
                            <X className="w-3 h-3 inline mr-1" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="font-medium text-gray-900 dark:text-warm-white">
                          {tool.name || tool.type}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {tool.description || tool.type}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {editingToolIndex !== index && (
                    <Tooltip content="Edit tool name and description">
                      <button
                        type="button"
                        onClick={() => startEditingTool(index)}
                        className="text-primary-500 hover:text-primary-700 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip content="Remove tool">
                    <button
                      type="button"
                      onClick={() => removeTool(index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Tooltip>
                </div>
              </div>

              {/* GoDaddy tool: show expandable example output */}
              {tool.type?.startsWith('godaddy_') && godaddyToolDefs[tool.type] && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => toggleToolExample(index)}
                    className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    <Code className="w-3 h-3 mr-1" />
                    {expandedToolExamples.has(index) ? 'Hide' : 'Show'} input &amp; example output
                    {expandedToolExamples.has(index) ? (
                      <ChevronDown className="w-3 h-3 ml-1" />
                    ) : (
                      <ChevronRight className="w-3 h-3 ml-1" />
                    )}
                  </button>
                  {expandedToolExamples.has(index) && (
                    <div className="mt-2 space-y-2">
                      {godaddyToolDefs[tool.type].input_description && (
                        <div>
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Input</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2 rounded">
                            {godaddyToolDefs[tool.type].input_description}
                          </p>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Example Output</p>
                          <button
                            type="button"
                            onClick={() => handleGenerateSchemaFromTool(tool.type, godaddyToolDefs[tool.type].example_output)}
                            className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium"
                            title="Create a new schema matching this tool's output shape"
                          >
                            <Sparkles className="w-3 h-3" />
                            Generate schema from this output
                          </button>
                        </div>
                        <pre className="text-xs bg-gray-800 text-gray-100 p-3 rounded overflow-x-auto">
                          {godaddyToolDefs[tool.type].example_output}
                        </pre>
                      </div>
                      {godaddyToolDefs[tool.type].notes && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                          {godaddyToolDefs[tool.type].notes}
                        </p>
                      )}
                      {generateSchemaError && (
                        <p className="text-xs text-red-600 dark:text-red-400">{generateSchemaError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {tool.type === 'web_search' && tool.user_location && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <MapPin className="w-4 h-4" />
                    <span>Location Settings</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label htmlFor={`city-${index}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        City
                      </label>
                      <input
                        id={`city-${index}`}
                        type="text"
                        value={tool.user_location.city || ''}
                        onChange={(e) => updateLocation(index, 'city', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="e.g., Ashburn"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor={`region-${index}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Region/State
                      </label>
                      <input
                        id={`region-${index}`}
                        type="text"
                        value={tool.user_location.region || ''}
                        onChange={(e) => updateLocation(index, 'region', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="e.g., Virginia"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor={`country-${index}`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Country
                      </label>
                      <input
                        id={`country-${index}`}
                        type="text"
                        value={tool.user_location.country || ''}
                        onChange={(e) => {
                          const formatted = formatCountryCode(e.target.value);
                          updateLocation(index, 'country', formatted);
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="e.g., US"
                        maxLength={2}
                      />
                      {tool.user_location.country && !validateCountryCode(tool.user_location.country) && (
                        <p className="mt-1 text-xs text-red-600">Country must be a 2-letter code (e.g., US, CA, GB)</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                    <AlertTriangle className="w-3 h-3" />
                    <span>Location helps provide more relevant search results</span>
                  </div>
                </div>
              )}

                             {tool.type === 'agent' && tool.agent_as_a_tool_id && (
                 <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                   <div className="flex items-center justify-between">
                     <button
                       type="button"
                       onClick={() => toggleAgentToolExpansion(index)}
                       className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300 font-medium hover:text-gray-900 dark:text-warm-white transition-colors"
                     >
                       {expandedAgentTools.has(index) ? (
                         <ChevronDown className="w-4 h-4 transform transition-transform duration-200" />
                       ) : (
                         <ChevronRight className="w-4 h-4 transform transition-transform duration-200" />
                       )}
                       <span>Tool Agent Details</span>
                     </button>
                     <div className="flex items-center space-x-2">
                       {(() => {
                         const agent = getAgentForTool(tool);
                         if (!agent) {
                           return (
                             <div className="flex items-center space-x-1 text-yellow-600 text-xs">
                               <AlertTriangle className="w-3 h-3" />
                               <span>Agent Not Found</span>
                             </div>
                           );
                         }
                         return (
                           <>
                             <Tooltip content="View full agent details">
                             <button
                               type="button"
                               onClick={() => {
                                 console.log('[ToolManagementSystem] View button clicked for tool:', tool);
                                 console.log('[ToolManagementSystem] Found agent for view:', agent);
                                 handleViewAgentDetails(agent);
                               }}
                               className="flex items-center space-x-1 text-primary-500 hover:text-primary-700 transition-colors text-xs"
                             >
                               <Eye className="w-3 h-3" />
                               <span>View</span>
                             </button>
                             </Tooltip>
                             <Tooltip content="Edit this tool agent">
                             <button
                               type="button"
                               onClick={() => {
                                 console.log('[ToolManagementSystem] Edit button clicked for tool:', tool);
                                 console.log('[ToolManagementSystem] Found agent for edit:', agent);
                                 handleEditAgent(agent);
                               }}
                               className="flex items-center space-x-1 text-green-500 hover:text-green-700 transition-colors text-xs"
                             >
                               <Edit className="w-3 h-3" />
                               <span>Edit</span>
                             </button>
                             </Tooltip>
                           </>
                         );
                       })()}
                     </div>
                   </div>
                   {expandedAgentTools.has(index) && (
                     <div className="mt-3 pl-6 space-y-2 text-sm">
                       <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
                         {(() => {
                           const agent = getAgentForTool(tool);
                           console.log('[ToolManagementSystem] Rendering expanded details for tool:', tool);
                           console.log('[ToolManagementSystem] Found agent:', agent);
                           
                           if (!agent) {
                             return (
                               <div className="space-y-3">
                                 <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                                   <div className="flex items-center space-x-2">
                                     <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                                     <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Agent Not Found</span>
                                   </div>
                                   <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                     The tool agent with ID {tool.agent_as_a_tool_id} could not be found. 
                                     It may have been deleted or is not available in the current project.
                                   </p>
                                   <div className="mt-2">
                                     <span className="font-medium text-gray-700 dark:text-gray-300">Tool Name:</span>
                                     <p className="text-gray-600 dark:text-gray-400">{tool.name || 'N/A'}</p>
                                   </div>
                                   <div className="mt-2">
                                     <span className="font-medium text-gray-700 dark:text-gray-300">Tool Description:</span>
                                     <p className="text-gray-600 dark:text-gray-400">{tool.description || 'N/A'}</p>
                                   </div>
                                 </div>
                               </div>
                             );
                           }
                           
                           return (
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                               <div>
                                 <span className="font-medium text-gray-700 dark:text-gray-300">Name:</span>
                                 <p className="text-gray-600 dark:text-gray-400">{agent.name}</p>
                               </div>
                               <div className="md:col-span-2">
                                 <span className="font-medium text-gray-700 dark:text-gray-300">Instructions:</span>
                                 <p className="text-gray-600 dark:text-gray-400 text-xs mt-1 bg-white dark:bg-cap-dark p-2 rounded border">
                                   {agent.instructions || 'No instructions provided'}
                                 </p>
                               </div>
                               <div>
                                 <span className="font-medium text-gray-700 dark:text-gray-300">Created:</span>
                                 <p className="text-gray-600 dark:text-gray-400 text-xs">
                                   {agent.created_at ? 
                                     new Date(agent.created_at).toLocaleDateString() : 'N/A'}
                                 </p>
                               </div>
                               <div>
                                 <span className="font-medium text-gray-700 dark:text-gray-300">Updated:</span>
                                 <p className="text-gray-600 dark:text-gray-400 text-xs">
                                   {agent.updated_at ? 
                                     new Date(agent.updated_at).toLocaleDateString() : 'N/A'}
                                 </p>
                               </div>
                             </div>
                           );
                         })()}
                       </div>
                     </div>
                   )}
                 </div>
               )}
            </div>
                ))}
              </>
            );
          })()}
        </div>
      )}

      {/* Add Tool Button */}
      {!showAddTool ? (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowAddTool(true)}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Built-in Tool</span>
          </button>
          <button
            type="button"
            onClick={() => setShowAgentToolSelector(true)}
            data-tutorial="btn-add-tool-agent"
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
          >
            <Bot className="w-4 h-4" />
            <span>Add Tool Agent</span>
          </button>
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-cap-dark">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Add New Tool</h4>
            <button
              type="button"
              onClick={() => { setShowAddTool(false); setNewLocation({ city: '', region: '', country: '' }); }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              ×
            </button>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
              <Globe className="w-4 h-4 text-primary-600" />
              <span className="font-medium">Web Search</span>
            </div>
            {newToolType === 'web_search' && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <MapPin className="w-4 h-4" />
                  <span>Location Settings (optional)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label htmlFor="add-city" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      City
                    </label>
                    <input
                      id="add-city"
                      type="text"
                      value={newLocation.city}
                      onChange={e => setNewLocation(loc => ({ ...loc, city: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Ashburn"
                    />
                  </div>
                  <div>
                    <label htmlFor="add-region" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Region/State
                    </label>
                    <input
                      id="add-region"
                      type="text"
                      value={newLocation.region}
                      onChange={e => setNewLocation(loc => ({ ...loc, region: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Virginia"
                    />
                  </div>
                  <div>
                    <label htmlFor="add-country" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Country
                    </label>
                    <input
                      id="add-country"
                      type="text"
                      value={newLocation.country}
                      onChange={e => {
                        const formatted = formatCountryCode(e.target.value);
                        setNewLocation(loc => ({ ...loc, country: formatted }));
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., US"
                      maxLength={2}
                    />
                    {newLocation.country && !validateCountryCode(newLocation.country) && (
                      <p className="mt-1 text-xs text-red-600">Country must be a 2-letter code (e.g., US, CA, GB)</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Location helps provide more relevant search results</span>
                </div>
              </div>
            )}

            {hasToolOfType(newToolType) && (
              <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  A {newToolType.replace('_', ' ')} tool is already added to this agent.
                </p>
              </div>
            )}
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={addSimplifiedTool}
                disabled={hasToolOfType(newToolType)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Web Search Tool
              </button>
              <button
                type="button"
                onClick={() => { setShowAddTool(false); setNewLocation({ city: '', region: '', country: '' }); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {tools.length === 0 && !showAddTool && (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <Globe className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No tools attached to this agent</p>
          <p className="text-xs">Tools enhance agent capabilities with external functionality</p>
        </div>
      )}

      {/* MCP Tools Section */}
      {currentProject && (
        <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center space-x-2 mb-3">
            <Server className="w-4 h-4 text-green-600" />
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">MCP Server Tools</h4>
          </div>
          <MCPToolSelector
            projectId={parseInt(currentProject.project_id)}
            selectedTools={tools}
            onToolsChange={onToolsChange}
          />
        </div>
      )}

      {/* Knowledge Base Search Section */}
      <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="flex items-center space-x-2 mb-3">
          <BookOpen className="w-4 h-4 text-amber-600" />
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Knowledge Base Search</h4>
        </div>
        <KnowledgeSearchToolSelector
          selectedTools={tools}
          onToolsChange={onToolsChange}
        />
      </div>

      {/* Agent Tool Selector Modal */}
      <Modal isOpen={showAgentToolSelector} onClose={() => setShowAgentToolSelector(false)}>
        {showAgentToolSelector && (
          <div data-modal-active="true">
            <AgentToolSelector
              availableAgents={availableAgentsForSelector}
              currentAgentId={currentAgentId}
              existingToolAgentIds={tools.filter(t => t.type === 'agent' && t.agent_as_a_tool_id).map(t => Number(t.agent_as_a_tool_id))}
              onAgentSelected={handleAgentToolSelected}
              autoSelectAgentId={lastCreatedToolAgentId}
              onClose={() => setShowAgentToolSelector(false)}
              onCreateToolAgent={handleCreateToolAgent}
            />
          </div>
        )}
      </Modal>

      {/* Create Tool Agent Modal */}
      <Modal isOpen={showCreateToolAgentModal} onClose={() => setShowCreateToolAgentModal(false)}>
        {showCreateToolAgentModal && (
          <div data-modal-active="true">
            <AgentFormModal
              models={models}
              availableAgents={availableAgentsForSelector}
              onSubmit={handleToolAgentCreated}
              onClose={() => setShowCreateToolAgentModal(false)}
              showToast={true}
              closeOnSubmit={true}
            />
          </div>
        )}
      </Modal>

             {/* Agent Details Modal */}
       <Modal isOpen={showAgentDetailsModal} onClose={handleAgentDetailsClose}>
         {showAgentDetailsModal && selectedAgentForDetails && (
           <div data-modal-active="true">
             <AgentDetailsModal
               agent={selectedAgentForDetails}
               models={[]}
               onClose={handleAgentDetailsClose}
               onEdit={() => handleEditAgent(selectedAgentForDetails)}
             />
           </div>
         )}
       </Modal>

       {/* Edit Agent Modal */}
       <Modal isOpen={showEditAgentModal} onClose={handleAgentEditClose}>
         {showEditAgentModal && selectedAgentForEdit && (
           <div data-modal-active="true">
             <AgentFormModal
               agent={selectedAgentForEdit}
               models={models}
               availableAgents={availableAgentsForSelector}
               onSubmit={handleAgentEditComplete}
               onClose={handleAgentEditClose}
               showToast={true}
               closeOnSubmit={true}
             />
           </div>
         )}
       </Modal>

       {/* Schema generation from tool output */}
       {generatingSchemaFor && (
         <GeneratedSchemaReviewModal
           open={true}
           onClose={() => setGeneratingSchemaFor(null)}
           existingModels={models}
           parentClassName={generatingSchemaFor.parentClassName}
           parentDescription={generatingSchemaFor.parentDescription}
           parentProperties={generatingSchemaFor.parentProperties}
           suggestedChildren={generatingSchemaFor.suggestedChildren}
           onSaveAll={handleGeneratedSchemasSave}
         />
       )}
    </div>
  );
};

export default ToolManagementSystem; 